"use client";

import { SessionProvider } from "next-auth/react";
import RevokedSessionGuard from "@/components/Security/RevokedSessionGuard";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import PostHogPageView from "./PostHogPageView";
import PostHogIdentity from "@/components/Tracking/PostHogIdentity";
import Tailwind4CapabilityProbe from "@/components/Tracking/Tailwind4CapabilityProbe";
import TimezoneSync from "@/components/ui/TimezoneSync/TimezoneSync";
import { ToastContainer } from "react-toastify";
import { LevelUpProvider } from "@/contexts/LevelUpContext";
import { SoundProvider } from "@/contexts/SoundContext";
import CheerPopin from "@/components/Cheers/CheerPopin";
import { MessengerNotificationsProvider } from "@/contexts/MessengerNotificationsContext";
import { CommerceSettingsProvider } from "@/contexts/CommerceSettingsContext";
import { PromotionModalProvider } from "@/contexts/PromotionModalContext";
import dynamic from "next/dynamic";
import { readConsent, syncPostHogConsent } from "@/lib/cookieConsent";

const LevelUpAnimation = dynamic(() => import("@/components/ui/Gamification/Level/LevelUpAnimation"), { ssr: false });
const ChangelogWidget = dynamic(() => import("@/components/Changelog/ChangelogWidget"), { ssr: false });
const WinbackOffer = dynamic(() => import("@/components/WinbackOffer/WinbackOffer"), { ssr: false });
const LinkConversionTracker = dynamic(() => import("@/components/Tracking/LinkConversionTracker/LinkConversionTracker"), { ssr: false });
const TinymceDraftCleaner = dynamic(() => import("@/components/Storage/TinymceDraftCleaner"), { ssr: false });

// Initialisation immédiate, à l'évaluation du bundle (avant l'hydratation et
// avant tout useEffect). Une init différée (requestIdleCallback) faisait
// perdre le premier $pageview de chaque session : posthog-js abandonne les
// capture() reçus avant init, donc les visiteurs qui rebondissaient sur la
// page d'entrée étaient totalement invisibles.
if (typeof window !== "undefined" && !posthog.__loaded) {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        // Reverse proxy même domaine (cf. rewrites dans next.config.mjs) :
        // les requêtes ne partent plus vers eu.i.posthog.com, bloqué par les
        // adblockers (très répandus chez notre audience de développeurs)
        api_host: "/ingest",
        ui_host: "https://eu.posthog.com",
        // Un profil "personne" n'est créé qu'à l'identification (connexion) :
        // moins coûteux et compteur de visiteurs uniques plus fiable
        person_profiles: "identified_only",
        // Consentement (CNIL) : tant que le visiteur n'a pas accepté la mesure
        // d'audience, PostHog compte les visites SANS cookie ni identification
        // (hachage côté serveur, « Cookieless server hash mode » à activer
        // dans le projet PostHog). L'accord rallume cookies, identify et
        // enregistrements de session. Voir lib/cookieConsent.js.
        cookieless_mode: "on_reject",
        capture_pageview: false, // capturé manuellement (PostHogPageView)
        capture_pageleave: true,
        before_send: (event) => {
            if (!event) return event;
            // Les événements d'identité passent toujours, même depuis une
            // page admin : les dropper casserait définitivement le merge
            // anonyme → personne et la mise à jour des propriétés sur
            // l'appareil concerné
            if (
                [
                    "$identify",
                    "$set",
                    "$create_alias",
                    "$merge_dangerously",
                    "$groupidentify",
                ].includes(event.event)
            ) {
                return event;
            }
            // Exclut la navigation admin des statistiques
            const url = event.properties?.["$current_url"];
            if (url) {
                try {
                    const pathname = new URL(url).pathname;
                    if (/^\/([a-z]{2}\/)?admin(\/|$)/.test(pathname)) {
                        return null;
                    }
                } catch (e) {
                    // URL invalide : on laisse passer l'événement
                }
            }
            return event;
        },
    });
    // Notre cookie de choix fait foi : PostHog garde sa propre trace du
    // consentement, qu'on réaligne avant le premier événement.
    syncPostHogConsent(posthog, readConsent());
}

export function Providers({ children }) {
    return (
        <>
            <ToastContainer
                position="bottom-right"
                autoClose={8000}
                theme="dark"
                closeOnClick={true}
            />
            <PHProvider client={posthog}>
                <PostHogPageView />
                <Tailwind4CapabilityProbe />
                <SessionProvider>
                    <PostHogIdentity />
                    <TimezoneSync />
                    <RevokedSessionGuard />
                    <SoundProvider>
                        <LevelUpProvider>
                            <MessengerNotificationsProvider>
                                <CommerceSettingsProvider>
                                    <PromotionModalProvider>
                                        {children}
                                        <LevelUpAnimation />
                                        <ChangelogWidget />
                                        <CheerPopin />
                                        <WinbackOffer />
                                        <LinkConversionTracker />
                                        <TinymceDraftCleaner />
                                    </PromotionModalProvider>
                                </CommerceSettingsProvider>
                            </MessengerNotificationsProvider>
                        </LevelUpProvider>
                    </SoundProvider>
                </SessionProvider>
            </PHProvider>
        </>
    );
}
